import {
  EarningsInsight,
  EarningsItemWithEntity,
  EarningsPeriod,
  Entity,
  mapEntity,
} from '@api';
import { TableCell } from '@design/components/table';
import { TanStackTableRoot } from '@design/components/tanstack-table/TanStackTable';
import { isElementScrollbarVisible } from '@helpers';
import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { EarningsDrawer } from '@terminal/views/theme-overview/tab-content/earnings/components/earnings-drawer/EarningsDrawer';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import clsx from 'clsx';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEarningsStore } from '../../useEarningsStore/useEarningsStore';
import {
  EARNINGS_COLUMN_ID,
  getCellHighlighted,
  QNA_COLUMN_ID,
  RELEASED_ON_COLUMN_ID,
  SELECTED_COLUMN_ID,
} from '../../utils/utils';
import { EarningsTableHeaderCell } from '../EarningsTableHeaderCell';
import styles from './EarningsTable.module.scss';

interface EarningsTableProps {
  columns: ColumnDef<EarningsPeriod>[];
  entity: Entity;
  earningsInsightByEarningsId: Map<string, EarningsInsight>;
}

export const EarningsTable = ({
  columns,
  entity,
  earningsInsightByEarningsId,
}: EarningsTableProps) => {
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    activeSortingState,
    rowSelection,
    isViewSelectedOnly,
    earningsData,
    earningExchange,
    resetAllFilterOptions,
  } = useEarningsStore();
  const { t } = useTranslation();
  const [drawerData, setDrawerData] = useState<
    EarningsItemWithEntity | undefined
  >();
  const { deleteQueryParam, setQueryParams } = useQueryParams();
  const { openDrawer } = useEarningsInsightDrawer();

  useEffect(
    () => setHasHorizontalScroll(isElementScrollbarVisible(containerRef)),
    [containerRef]
  );

  const table = useReactTable({
    data: earningsData,
    columns,
    enableMultiSort: false,
    enableRowSelection: true,
    state: {
      sorting: activeSortingState,
      rowSelection,
      columnPinning: {
        left: [SELECTED_COLUMN_ID, EARNINGS_COLUMN_ID, QNA_COLUMN_ID],
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: data => data.reporting_date,
  });

  const tableRows = table.getRowModel().rows;
  const tableRowsToDisplay = isViewSelectedOnly
    ? tableRows.filter(row => row.getIsSelected())
    : tableRows;

  const isCellNumber = (columnId: string) => {
    return (
      columnId !== EARNINGS_COLUMN_ID && columnId !== RELEASED_ON_COLUMN_ID
    );
  };

  const getIsPinnedVertically = (column: Column<EarningsPeriod>) =>
    hasHorizontalScroll && !!column.getIsPinned();

  const onRowClick = async (row: Row<EarningsPeriod>) => {
    setDrawerData({
      ...row.original,
      entity_tag: entity.tag,
      entity: mapEntity(entity),
      exchange: earningExchange,
    });

    const insight = earningsInsightByEarningsId.get(row.original.id);
    if (insight) {
      openDrawer({
        entityTag: insight.entity_tag,
        earningsId: insight.earnings_id,
        subtab:
          insight.type === 'preview' ? SubTabsType.Preview : SubTabsType.Recap,
        source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      });
    } else {
      setQueryParams(
        {
          [queryKeys.activeDrawer]: queryValues.earningsDrawer,
        },
        { replace: true }
      );
    }
  };

  const onDrawerClose = () => {
    deleteQueryParam([queryKeys.activeDrawer], { replace: true });
    setDrawerData(undefined);
  };

  if (!tableRowsToDisplay.length) {
    return (
      <div className={styles.errorMessageAndResetButtonWrapper}>
        <ErrorMessageWrapper
          variant="empty"
          displayIcon
          title={t('widget:earnings.empty.title')}
          subTitle={t('widget:earnings.empty.emptyFilteredResultsSubTitle')}
          showBorder={false}
          buttonProps={{
            label: t('widget:earnings.resetAll'),
            onClick: resetAllFilterOptions,
            variant: 'empty',
            size: 'small',
            iconName: 'Reset',
            className: styles.styledButton,
            'data-testid': 'earnings-reset-all-filters-button',
          }}
        />
      </div>
    );
  }

  return (
    <>
      <EarningsDrawer drawerData={drawerData} onDrawerClose={onDrawerClose} />
      <div className={styles.tableScrollContainer} ref={containerRef}>
        <TanStackTableRoot data-testid="earnings-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup, index, arr) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const isLastHeaderGroup = index === arr.length - 1;
                  return (
                    <EarningsTableHeaderCell
                      key={header.id}
                      header={header}
                      isLastHeaderGroup={isLastHeaderGroup}
                      isPinnedVertically={getIsPinnedVertically(header.column)}
                      isHighlighted={getCellHighlighted(header.column.id)}
                    />
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {tableRowsToDisplay.map(row => (
              <tr key={row.id} onClick={() => onRowClick(row)}>
                {row.getVisibleCells().map((cell, idx) => {
                  const isPinned = getIsPinnedVertically(cell.column);
                  const isHighlighted = getCellHighlighted(cell.column.id);

                  return (
                    <TableCell
                      key={cell.id}
                      className={clsx(styles.styledTableCell, {
                        [styles.cellHighlighted]: isHighlighted,
                        [styles.cellPinned]: isPinned,
                        [styles.cellLastPinned]:
                          isPinned && cell.column.getIsLastColumn('left'),
                      })}
                      isNumber={isCellNumber(cell.column.id)}
                      alignment={idx > 2 ? 'end' : 'start'}
                      data-earnings-cell
                      style={
                        {
                          '--cell-left': isPinned
                            ? `${cell.column.getStart('left')}px`
                            : undefined,
                          '--cell-right': isPinned
                            ? `${cell.column.getAfter('right')}px`
                            : undefined,
                        } as CSSProperties
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </TanStackTableRoot>
      </div>
    </>
  );
};
